import React from "react"
import autobind from "class-autobind"
import { IJobDocument, ISelectedFilterTemplateId, EDefaultFilterValues, ITimecardFullData, ITimeTotals, IDate, IPayrollPayCalendar, IUserFilter } from "../../../models"
import TimeActionBar from "../../../components/common/TimeActionBar/TimeActionBar"
import PayCalendarSelector from "../../../components/pay_calendar/PayCalendarSelector"
import moment from "moment"
import * as api from "../../../api"
import TimecardsBody from "../../../components/timecard/timecard_list/TimecardsBody"
import * as payrollHelper from "../../../helpers/payrollHelper"
import AppStateStore from "../../../stores/AppStateStore"
import * as _ from 'lodash'

interface IProps {
    job: IJobDocument
    referenceDate: IDate
    selectedPayrollPayCalendar: IPayrollPayCalendar
    selectedFilterTemplateId: ISelectedFilterTemplateId
    handleChangeReferenceDate(referenceDate: IDate): void
    handleChangePayrollPayCalendar(selectedPayrollPayCalendar: IPayrollPayCalendar): void
    userFilter: IUserFilter
    lastFilterUpdate?: number
}
interface IState {
    openPunchDetailReportDialog: boolean
    openTimecardSummaryReportDialog: boolean
    TimecardAuditReportDialogOpen: boolean
    openEstimatedTimeReportDialog: boolean
    openInOutReportDailog: boolean
    referenceDate: IDate
    selectedPayrollPayCalendar: IPayrollPayCalendar
    timecards: ITimecardFullData[]
    selectedTimecards: ITimecardFullData[]
    total: number
    offset: number
    limit: number
    timeTotals: ITimeTotals
    expandAllTimecards: boolean
}

export default class JobTimecards extends React.Component<IProps, IState> {
    private _isMounted: boolean = false;

    constructor(props: IProps) {
        super(props)
        this.state = {
            openPunchDetailReportDialog: false,
            openTimecardSummaryReportDialog: false,
            TimecardAuditReportDialogOpen: false,
            openEstimatedTimeReportDialog: false,
            openInOutReportDailog: false,
            referenceDate: props.referenceDate,
            selectedPayrollPayCalendar: props.selectedPayrollPayCalendar,
            timecards: [],
            selectedTimecards: [],
            total: 0,
            offset: 0,
            limit: 20,
            timeTotals: null,
            expandAllTimecards: false,
        }
        autobind(this)
    }

    componentDidMount() {
        this._isMounted = true;
        // Only load timecards if we have filter data
        if (this.props.selectedFilterTemplateId !== null) {
            this.loadTimecards()
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    componentDidUpdate(prevProps: IProps, prevState: IState) {
        // Specific check for userFilter changes
        const hasUserFilterChanged = !_.isEqual(prevProps.userFilter, this.props.userFilter);
        
        // Check for any relevant prop changes that should trigger a reload
        const shouldReload = 
            prevProps.selectedFilterTemplateId !== this.props.selectedFilterTemplateId ||
            hasUserFilterChanged ||
            !moment(prevProps.referenceDate).isSame(this.props.referenceDate) ||
            !_.isEqual(prevProps.selectedPayrollPayCalendar, this.props.selectedPayrollPayCalendar) ||
            prevProps.lastFilterUpdate !== this.props.lastFilterUpdate;

        if (shouldReload) {
            this.loadTimecards();
        }
    }


    loadTimecards(cb: Function = null) {
        if (!this._isMounted) return;

        const currTop = window.scrollY
        const currLeft = window.scrollX
        AppStateStore.setProgressSnackbarContent("loading")
        
        setTimeout(() =>
            api.getJobTimecards(
                this.props.job.Id,
                payrollHelper.getPayGroupIdsByPayrollPayCalendar(this.state.selectedPayrollPayCalendar),
                moment(this.state.referenceDate).startOf("day").utc(true).add(this.state.selectedPayrollPayCalendar.PayPeriodDayStartTime24Hour, "hours").format(),
                this.state.offset / this.state.limit,
                this.state.limit,
                this.props.userFilter
            ).then(res => {
                if (this._isMounted) {
                    this.setState({
                        timecards: res.data.Documents,
                        total: res.data.TotalFilterDocumentCount,
                        timeTotals: res.data.TimeTotals,
                    }, () => {
                        AppStateStore.getProgressSnackbarContent() === "loading" &&
                            AppStateStore.setProgressSnackbarContent(null)
                        window.scrollTo(currLeft, currTop)
                        cb && typeof cb === "function" && cb()
                    })
                }
            }),
            100
        )
    }

    render() {
        return (
            <div className="detail-tab">
                <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
                    <TimeActionBar
                        referenceDate={this.state.referenceDate}
                        handleChangeTimeRange={(startDate, endDate) => {
                            this.setState({ referenceDate: startDate }, () => {
                                this.loadTimecards()
                                this.props.handleChangeReferenceDate(this.state.referenceDate)
                            })
                        }}
                        startDayOfWeek={this.state.selectedPayrollPayCalendar.PayPeriodStartDay}
                        timeBarViewType={"week"}
                    />
                    <PayCalendarSelector
                        selectedPayrollPayCalendar={this.state.selectedPayrollPayCalendar}
                        handleSelect={selectedPayrollPayCalendar => {
                            this.props.handleChangePayrollPayCalendar(selectedPayrollPayCalendar)
                            this.setState({ selectedPayrollPayCalendar }, () => this.loadTimecards())
                        }}
                    />
                </div>
                <div style={{ borderBottom: "solid 1px rgb(224, 224, 224)", height: 1 }} />
                <TimecardsBody
                    job={this.props.job}
                    referenceDate={this.state.referenceDate}
                    userFilter={this.props.userFilter}
                    timecards={this.state.timecards}
                    totalTimecards={this.state.total}
                    total={this.state.total}
                    offset={this.state.offset}
                    limit={this.state.limit}
                    limitOptions={[20, 50, 100]}
                    selectedPayrollPayCalendar={this.state.selectedPayrollPayCalendar}
                    onPageClick={offset => this.setState({ offset }, () => this.loadTimecards())}
                    onLimitChange={limit => this.setState({ offset: 0, limit }, () => this.loadTimecards())}
                    reLoadTimecards={this.loadTimecards}
                    showTimecardDetails={true}
                />
            </div>
        )
    }
}
